/* ============================================================
   DetailPageShell — guscio UNICO e COMPATTO per le pagine di dettaglio
   pratica (RDI/DDT/BSC/Rinascente).

   Una sola barra in alto (header + azioni in un'unica riga compatta):
     [← Lista] Titolo  id  [badge…]  [meta…]  ⟶  [Approva][Riprocessa][Rifiuta][extra]
   Sotto, il body a scroll INTERNO. Obiettivi:
   - densità: niente card azioni enorme, tutto btn-sm, padding ridotto;
   - coerenza: stesso header/ordine/varianti per tutti i verticali
     (Approva=btn-primary, Riprocessa=btn-outline, Rifiuta=btn-danger);
   - altezza fissa (100vh - padding main) → barra sempre in cima, il
     contenuto scorre nel body, non nella window;
   - tabelle: avvolgerle in <div className="table-scroll"> → scroll
     orizzontale, mai troncamento.
   ============================================================ */
import { useNavigate } from "react-router-dom";
import type { ReactNode } from "react";

export interface BarAction {
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  label?: string;
}

function ActionBtn({ variant, icon, defaultLabel, action }: {
  variant: "primary" | "outline" | "danger";
  icon: string;
  defaultLabel: string;
  action: BarAction;
}) {
  return (
    <button className={`btn btn-${variant} btn-sm`} onClick={action.onClick} disabled={action.disabled} title={action.title}>
      <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{icon}</span>
      {action.label ?? defaultLabel}
    </button>
  );
}

export function DetailPageShell({
  backTo,
  onBack,
  backLabel = "Indietro",
  title,
  titleId,
  badges,
  meta,
  approve,
  reprocess,
  reject,
  extraActions,
  readOnlyMessage,
  children,
}: {
  backTo?: string;
  onBack?: () => void;
  backLabel?: string;
  title: ReactNode;
  titleId?: string;
  badges?: ReactNode;
  meta?: ReactNode;            // es. completezza %, fornitore — compatto
  approve?: BarAction;
  reprocess?: BarAction;
  reject?: BarAction;
  extraActions?: ReactNode;
  readOnlyMessage?: ReactNode;
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const hasActions = approve || reprocess || reject || extraActions;
  return (
    <div className="detail-shell">
      <div className="detail-shell__bar">
        {(backTo != null || onBack) && (
          <button className="btn btn-outline btn-sm" onClick={() => (onBack ? onBack() : navigate(backTo!))}>
            <span className="material-symbols-outlined" style={{ fontSize: 16, verticalAlign: "middle" }}>arrow_back</span>
            <span style={{ verticalAlign: "middle", marginLeft: 4 }}>{backLabel}</span>
          </button>
        )}
        <h1 className="detail-shell__title">{title}</h1>
        {titleId && <code className="detail-shell__id" title={titleId}>{titleId.slice(0, 8)}</code>}
        {badges}
        {meta && <div className="detail-shell__meta">{meta}</div>}
        <div className="detail-shell__spacer" />
        {hasActions ? (
          <div className="detail-shell__actions">
            {approve && <ActionBtn variant="primary" icon="check_circle" defaultLabel="Approva" action={approve} />}
            {reprocess && <ActionBtn variant="outline" icon="autorenew" defaultLabel="Riprocessa" action={reprocess} />}
            {reject && <ActionBtn variant="danger" icon="cancel" defaultLabel="Rifiuta" action={reject} />}
            {extraActions}
          </div>
        ) : readOnlyMessage ? (
          <div className="text-muted text-xs">{readOnlyMessage}</div>
        ) : null}
      </div>
      <div className="detail-shell__body">{children}</div>
    </div>
  );
}
